import * as React from 'react';
import { PageSection, Title, Card, CardBody, CardTitle, CardFooter, Button, Modal, ModalVariant, Wizard, WizardStep, WizardHeader, List, ListItem, Alert } from '@patternfly/react-core';
import ExternalLinkSquareAltIcon from '@patternfly/react-icons/dist/esm/icons/external-link-square-alt-icon';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSiteConfig } from '../config/SiteConfigContext';
import { startCollection } from '../Home/startCollection';

const logoutUrl = 'https://login.microsoftonline.com/common/oauth2/v2.0/logout';

interface OffboardingPageProps {
  taskDefId: string;
  heading: string;
}

export const OffboardingPage: React.FunctionComponent<OffboardingPageProps> = ({ taskDefId, heading }) => {
  const site = useSiteConfig();
  if (!site) {
    return (
      <PageSection>
        <Title headingLevel="h1" size="xl">{heading} Offboarding</Title>
        <br />
        <Alert variant="info" title="No services currently available" isInline>
          <p>There are no services currently available for this site.</p>
        </Alert>
      </PageSection>
    );
  }
  const check = site.checks.find((item) => item.taskDefId === taskDefId);
  const productName = site.productName;
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [step, setStep] = React.useState(1);
  const [hasLoggedOut, setHasLoggedOut] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stepParam = params.get('step');
    if (stepParam) {
      const stepNumber = parseInt(stepParam, 10);
      if (!isNaN(stepNumber) && stepNumber >= 1 && stepNumber <= 5) {
        setStep(stepNumber);
        setIsModalOpen(true);
      }
    }
  }, [location]);

  const handleStepChange = (_event: any, currentStep: any) => {
    const newStep = Number(currentStep.id);
    setStep(newStep);
    navigate(`${location.pathname}?step=${newStep}`, { replace: true });
  };

  const close = () => {
    setIsModalOpen(false);
    navigate(location.pathname, { replace: true });
  };

  const startOffboard = async () => {
    if (!check) {
      return;
    }
    try {
      const url = await startCollection(check.taskDefId, 'offboard');
      window.location.href = url;
    } catch (err: any) {
      setError(err?.message || 'Unable to start offboarding');
    }
  };

  return (
    <PageSection>
      <Title headingLevel="h1" size="xl">{productName} {heading} Offboarding</Title>
      <br />
      <Card>
        <CardTitle>Revoke access for {check?.title || heading}</CardTitle>
        <CardBody>
          <p>This will remove the {productName} application's access to your tenant.</p>
          <p>After removal, {productName} will no longer be able to access this data source.</p>
        </CardBody>
        <CardFooter>
          <Button variant="danger" onClick={() => {
            setStep(1);
            navigate(`${location.pathname}?step=1`, { replace: true });
            setIsModalOpen(true);
          }}>
            Revoke Access
          </Button>
        </CardFooter>
      </Card>

      <Modal isOpen={isModalOpen} variant={ModalVariant.medium} onEscapePress={close}>
        <Wizard
          height={500}
          onClose={close}
          title={`${heading} wizard`}
          startIndex={step}
          onStepChange={handleStepChange}
          isVisitRequired
          header={
            <WizardHeader
              onClose={close}
              title={`Revoke ${heading} Access`}
              description={`Follow these steps to remove ${productName}'s access to your environment.`}
            />
          }
        >
          <WizardStep name="Introduction" id="1">
            <List>
              <ListItem>This wizard will guide you through revoking {productName}'s access.</ListItem>
              <ListItem>You can always re-enable access by running the {productName} process again.</ListItem>
            </List>
          </WizardStep>
          <WizardStep name="Accept permissions" id="2" footer={{ isNextDisabled: true }}>
            <List>
              <ListItem>Follow the link below to be redirected to Microsoft.</ListItem>
              <ListItem>Write permissions are requested so {productName} can delete its own service principal.</ListItem>
            </List>
            <br />
            <Button variant="danger" icon={<ExternalLinkSquareAltIcon />} iconPosition="end" onClick={() => void startOffboard()}>
              Remove Application
            </Button>
            {error && <p style={{ marginTop: '0.5rem' }}>{error}</p>}
          </WizardStep>
          <WizardStep name="Application removed" id="3">
            <p>The {productName} application has been successfully removed from your environment.</p>
          </WizardStep>
          <WizardStep name="Logout" id="4" footer={{ isNextDisabled: !hasLoggedOut }}>
            <p>To revoke session cookies used during offboarding, please logout from Microsoft before proceeding:</p>
            <br />
            <Button
              variant="primary"
              icon={<ExternalLinkSquareAltIcon />}
              iconPosition="end"
              onClick={() => {
                window.open(logoutUrl, 'Microsoft Logout', 'width=500,height=600,left=100,top=100');
                setHasLoggedOut(true);
              }}
            >
              Logout from Microsoft
            </Button>
          </WizardStep>
          <WizardStep name="Access Revoked" id="5" footer={{ nextButtonText: 'Finish', onNext: close }}>
            <b>Success!</b>
            <br /><br />
            {productName}'s access has been revoked.
          </WizardStep>
        </Wizard>
      </Modal>
    </PageSection>
  );
};
